import { ApolloError } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { useCallback } from 'react'
import { reduceNotNull } from 'src/utils/utils'
import { useCoreErrorMessage } from './core-error-message/core-error-message'
import { CoreErrorCode, DocumentErrorCode, coreErrorCodes, documentErrorCodes } from './error-handler.backend-types'
import { useDocumentErrorMessage } from './document-error-message/document-error-message'

export function getCoreErrorCodeFromGraphQLError(error: GraphQLError): CoreErrorCode | undefined {
  return coreErrorCodes.find((c) => c === error.extensions['code']) || undefined
}

export function getDocumentErrorCodeFromGraphQLError(error: GraphQLError): DocumentErrorCode | undefined {
  return documentErrorCodes.find((c) => c === error.extensions['code']) || undefined
}

export function getCoreErrorCodesFromApolloError(error: ApolloError): CoreErrorCode[] {
  return reduceNotNull(error.graphQLErrors.map((e) => getCoreErrorCodeFromGraphQLError(e)))
}

export function useApolloErrorMessages() {
  const getGraphQLErrorMessage = useGraphQLErrorMessage()

  const getApolloErrorMessages = useCallback(
    function getApolloErrorMessages(error: ApolloError): string[] {
      const graphQLErrors = error.graphQLErrors?.map((e) => getGraphQLErrorMessage(e)) || []
      if (graphQLErrors.length > 0) {
        return graphQLErrors
      }
      const clientErrors = error.clientErrors?.map((e) => e.message) || []
      if (clientErrors.length > 0) {
        return clientErrors
      }

      const networkError = error.networkError?.message
      if (networkError) {
        return [networkError]
      }

      const message = error.message
      if (message) {
        return [message]
      }

      return []
    },
    [getGraphQLErrorMessage],
  )

  return getApolloErrorMessages
}

export function useGraphQLErrorMessage() {
  const getCoreErrorMessage = useCoreErrorMessage()
  const getDocumentErrorMessage = useDocumentErrorMessage()

  const getGraphQLErrorMessage = useCallback(
    function getGraphQLErrorMessage(error: GraphQLError): string {
      const coreErrorCode = getCoreErrorCodeFromGraphQLError(error)
      if (!coreErrorCode) {
        return error.message
      }
      const details = error.extensions['details']
      const coreErrorMessage = getCoreErrorMessage(coreErrorCode, details)
      if (!coreErrorMessage) {
        const documentErrorCode = getDocumentErrorCodeFromGraphQLError(error)
        if (!documentErrorCode) {
          return error.message
        }
        return getDocumentErrorMessage(documentErrorCode, details) ?? error.message
      }
      return coreErrorMessage ?? error.message
    },
    [getCoreErrorMessage, getDocumentErrorMessage],
  )

  return getGraphQLErrorMessage
}

export function includeErrorCode(error: any, code: CoreErrorCode): boolean {
  if (error === null || error === undefined) return false
  if (error instanceof ApolloError) return getCoreErrorCodesFromApolloError(error).includes(code)
  if (error instanceof GraphQLError) return code === getCoreErrorCodeFromGraphQLError(error)
  return false
}
